import { callable } from '../callable';

export type ProgramEnrollmentStatus = 'active' | 'on_leave' | 'graduated' | 'withdrawn';

/** Mirrors `toDto` in apps/functions/src/sis/program-enrollment.functions.ts. */
export interface ProgramEnrollmentDto {
  id: string;
  studentId: string;
  programId: string;
  intakeTermId: string;
  status: ProgramEnrollmentStatus;
  expectedGraduationTermId: string | null;
  catalogYear: string | null;
  notes: string | null;
  startedAt: string;
  endedAt: string | null;
  /** Por qué terminó (el motivo del retiro). Opcional: el servidor viejo no lo manda. */
  endReason?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DegreeAuditCompletedCourse {
  courseId: string;
  courseName: string;
  termId: string;
  grade: number | null;
  credits: number;
}

export interface DegreeAuditPendingCourse {
  courseId: string;
  courseName: string;
  credits: number;
  recommendedTermOffset: number;
  prerequisitesMet: boolean;
}

/** Mirrors the DegreeAudit interface in degree-audit.use-cases.ts. */
export interface DegreeAuditDto {
  programEnrollmentId: string;
  status: ProgramEnrollmentStatus;
  catalogYear: string | null;
  creditsEarned: number;
  creditsRequired: number;
  percentComplete: number;
  completed: DegreeAuditCompletedCourse[];
  pending: DegreeAuditPendingCourse[];
  offTrack: Array<{ courseId: string; courseName: string; reason: string }>;
  recommendedNextTerm: Array<{ courseId: string; courseName: string; termOffset: number }>;
}

// EL TIPO SE IMPORTA, NO SE COPIA. Estaba escrito acá a mano y era el mismo de
// `@dosfilos/application`, que ya es dependencia de este paquete. Con dos copias
// un campo nuevo del informe aparece en el servidor y la pantalla ni se entera:
// pasó con `state`, que el servidor empezó a mandar y acá no existía.
import type {
  TeachingCoverageRow,
  TeachingCoverageReport,
  SectionWorkDetail,
  StudentFollowUpReport,
  StudentFollowUpRow,
  WorkItem,
} from '@dosfilos/application';
export type {
  TeachingCoverageRow,
  TeachingCoverageReport,
  SectionWorkDetail,
  StudentFollowUpReport,
  StudentFollowUpRow,
  WorkItem,
};

export interface CapacityReportRow {
  courseId: string;
  courseName: string;
  studentCount: number;
}

export interface CapacityReport {
  programId: string;
  activeStudents: number;
  rows: CapacityReportRow[];
}

export const programEnrollmentApi = {
  listStudentProgramEnrollments: async (params: {
    tenantId: string;
    studentId: string;
  }): Promise<{ items: ProgramEnrollmentDto[] }> => {
    const fn = callable<typeof params, { items: ProgramEnrollmentDto[] }>(
      'listStudentProgramEnrollments',
    );
    return (await fn(params)).data;
  },

  getDegreeAudit: async (params: {
    tenantId: string;
    studentId: string;
    programId: string;
  }): Promise<DegreeAuditDto> => {
    const fn = callable<typeof params, DegreeAuditDto>('getDegreeAudit');
    return (await fn(params)).data;
  },

  listProgramCohort: async (params: {
    tenantId: string;
    programId: string;
    intakeTermId: string;
  }): Promise<{ items: ProgramEnrollmentDto[] }> => {
    const fn = callable<typeof params, { items: ProgramEnrollmentDto[] }>('listProgramCohort');
    return (await fn(params)).data;
  },

  getCapacityReport: async (params: {
    tenantId: string;
    programId: string;
  }): Promise<CapacityReport> => {
    const fn = callable<typeof params, CapacityReport>('getCapacityReport');
    return (await fn(params)).data;
  },

  /** Cobertura de registro del período: por sección, cuánto de lo ya vencido
   *  está efectivamente registrado. Las definiciones viven en el caso de uso. */
  getTeachingCoverage: async (params: {
    tenantId: string;
    termId: string;
  }): Promise<TeachingCoverageReport> => {
    const fn = callable<typeof params, TeachingCoverageReport>('getTeachingCoverage');
    return (await fn(params)).data;
  },

  /** Las tres listas que hay debajo del agregado de una sección. */
  getSectionWorkDetail: async (params: {
    tenantId: string;
    termId: string;
    sectionId: string;
  }): Promise<SectionWorkDetail> => {
    const fn = callable<typeof params, SectionWorkDetail>('getSectionWorkDetail');
    return (await fn(params)).data;
  },

  /** El mismo trabajo del período, agrupado por alumno. */
  getStudentFollowUp: async (params: {
    tenantId: string;
    termId: string;
  }): Promise<StudentFollowUpReport> => {
    const fn = callable<typeof params, StudentFollowUpReport>('getStudentFollowUp');
    return (await fn(params)).data;
  },

  /** Entrega 4: pause (onLeave=true) or reactivate (onLeave=false) an enrollment. */
  /** Registrar a una persona en un programa, sin admisión (H5). */
  create: async (params: {
    tenantId: string;
    studentId: string;
    programId: string;
    intakeTermId: string;
    /** Registrar aunque no haya completado el programa en que éste se apoya. */
    stackEntryOverride?: { reason: string };
  }): Promise<{ id: string; created: boolean }> => {
    const fn = callable<typeof params, { id: string; created: boolean }>('createProgramEnrollment');
    return (await fn(params)).data;
  },

  /** Retirar de un programa, con motivo (H5). Si la persona entró por una
   *  admisión aceptada a ese programa, el servidor lo rechaza con
   *  `program_enrollment.withdraw_via_admission` y su `admissionId`. */
  withdraw: async (params: {
    tenantId: string;
    programEnrollmentId: string;
    reason: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof params, { success: boolean }>('withdrawFromProgram');
    return (await fn(params)).data;
  },

  /** Antes de retirar: qué NO cambia el retiro — planes de pago activos de ese
   *  programa (`null` con Finanzas apagado), secciones vigentes de ese
   *  programa y si el retiro va a Admisiones. Solo cifras. */
  getWithdrawalPreview: async (params: {
    tenantId: string;
    programEnrollmentId: string;
  }): Promise<{
    activePaymentPlans: number | null;
    enrolledSections: number;
    viaAdmission: { admissionId: string } | null;
  }> => {
    const fn = callable<
      typeof params,
      {
        activePaymentPlans: number | null;
        enrolledSections: number;
        viaAdmission: { admissionId: string } | null;
      }
    >('getProgramWithdrawalPreview');
    return (await fn(params)).data;
  },

  setLeave: async (params: {
    tenantId: string;
    programEnrollmentId: string;
    onLeave: boolean;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof params, { success: boolean }>('setProgramEnrollmentLeave');
    return (await fn(params)).data;
  },

  /** Graduate one enrollment. The person's lifecycle stage (student ↔ alumni)
   *  is derived server-side from all their enrollments — see
   *  `person-lifecycle-sync.ts`. */
  markGraduated: async (params: {
    tenantId: string;
    programEnrollmentId: string;
    graduationTermId?: string;
    /** Why the student is graduating without completing the plan. The server
     *  measures completion itself and REFUSES without this when the plan is
     *  incomplete — sending it is not what makes it an override. */
    incompleteReason?: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof params, { success: boolean }>('markProgramGraduated');
    return (await fn(params)).data;
  },
};
